/**
 * Qué escribe cada propuesta confirmada (puro). La web lo escribe en IndexedDB y el servidor
 * (WhatsApp) en su base de datos: mismas reglas, mismo resultado y mismos mensajes.
 *
 * - Lanza DomainError si los datos ya no lo permiten (hábito borrado, fecha futura…).
 * - No comprueba los límites del plan: la web los comprueba antes y el servidor los
 *   impone al aplicar las escrituras.
 * - `show_today` y `start_focus` no escriben registros: son acciones de la interfaz (null).
 */
import { PLAN_LIMITS, countActiveHabits, type PlanId } from '@habitos/protocol';
import type { LocalDate } from '@habitos/engine';
import type { AppData, Ctx } from '../domain/data';
import { itemsOf } from '../domain/notesSearch';
import { dayTotal, DomainError, draftFromHabit, emptyDraft, habitEditPatch, logWrites, newHabitRecord, PlanLimitError, type RecordWrite } from '../domain/records';
import { keyAfter } from '../lib/fractional';
import type { Proposal } from './interpreter';

export interface PlannedWrites {
  writes: RecordWrite[];
  /** Clave de texto del resultado y sus valores. */
  message: string;
  params?: Record<string, string | number>;
}

export interface WriteEnv {
  data: AppData;
  ctx: Ctx;
  newId: () => string;
  now: Date;
  /** Hay un canal de aviso activo (push en este dispositivo o mensajería): cambia el mensaje. */
  pushActive: boolean;
}

/** Límites del plan que se comprueban antes de escribir (el servidor también los impone). */
export function checkPlanLimits(p: Proposal, data: AppData, plan: PlanId): void {
  const limits = PLAN_LIMITS[plan];
  if ((p.kind === 'create_habit' || p.kind === 'create_task') && countActiveHabits(data.habits) >= limits.activeHabits) throw new PlanLimitError(limits.activeHabits);
  if (p.kind === 'set_reminder') {
    const existing = data.reminders.filter((r) => r.habitId === p.habitId && !r.deletedAt).length;
    if (existing === 0 && limits.remindersPerHabit < 1) throw new PlanLimitError(limits.remindersPerHabit);
  }
}

export interface PlanRoom {
  /** Hábitos activos que añade la propuesta. */
  adds: number;
  active: number;
  limit: number;
  /** Espacios libres antes de aplicar la propuesta. */
  left: number;
}

/** Cuántos hábitos activos añade una propuesta y cuántos espacios deja el plan. Es solo un aviso previo: el límite se vuelve a comprobar al guardar. */
export function planRoom(items: Proposal[], data: AppData, plan: PlanId): PlanRoom {
  const adds = items.filter((p) => p.kind === 'create_habit' || p.kind === 'create_task').length;
  const limit = PLAN_LIMITS[plan].activeHabits;
  const active = countActiveHabits(data.habits);
  return { adds, active, limit, left: Math.max(0, limit - active) };
}

export function proposalWrites(p: Proposal, env: WriteEnv): PlannedWrites | null {
  const { data, ctx } = env;
  const nowIso = env.now.toISOString();
  const habit = (id: string) => {
    const h = data.habits.find((x) => x.id === id && !x.deletedAt);
    if (!h) throw new DomainError('not_found');
    return h;
  };
  const sortKey = () =>
    keyAfter(
      data.habits
        .filter((h) => !h.deletedAt)
        .map((h) => h.sortKey)
        .sort()
        .pop() ?? null,
    );
  const reminder = (habitId: string, time: string, days: number[] | null): RecordWrite => ({
    entity: 'reminders',
    id: env.newId(),
    patch: { habitId, time, days, enabled: true, deletedAt: null },
  });

  switch (p.kind) {
    case 'create_habit': {
      const id = env.newId();
      const draft = {
        ...emptyDraft(ctx.today, p.habitKind),
        name: p.name,
        goalType: p.goalType,
        target: p.target,
        unit: p.unit,
        schedule: p.schedule,
        defaultIncrement: p.unit === 'min' ? 5 : p.unit === 'ml' ? 250 : 1,
      };
      const writes: RecordWrite[] = [{ entity: 'habits', id, patch: newHabitRecord(draft, sortKey(), nowIso) }];
      if (!p.reminder) return { writes, message: 'assistant.done.habit', params: { name: p.name } };
      writes.push(reminder(id, p.reminder.time, p.reminder.days));
      return { writes, message: env.pushActive ? 'assistant.done.habitReminderActive' : 'assistant.done.habitReminderSaved', params: { name: p.name, time: p.reminder.time } };
    }
    case 'create_task': {
      const id = env.newId();
      const writes: RecordWrite[] = [{ entity: 'habits', id, patch: newHabitRecord({ ...emptyDraft(ctx.today, 'task'), name: p.name, dueDate: p.dueDate, dueTime: p.dueTime }, sortKey(), nowIso) }];
      const withReminder = p.reminder && p.dueTime;
      if (withReminder) writes.push(reminder(id, p.dueTime!, null));
      // Solo se afirma que habrá aviso si de verdad puede enviarse.
      const message = withReminder ? (env.pushActive ? 'assistant.done.reminderActive' : 'assistant.done.reminderSaved') : 'assistant.done.task';
      return { writes, message, params: { name: p.name } };
    }
    case 'add_to_list': {
      const writes: RecordWrite[] = [];
      let listId = p.listId;
      if (!listId) {
        listId = env.newId();
        writes.push({ entity: 'notes', id: listId, patch: { kind: 'list', title: p.listName.trim(), body: null, url: null, date: null, tags: [], habitIds: [], pinned: false, createdAt: nowIso, deletedAt: null } });
      }
      let last = itemsOf(data.listItems, listId).pop()?.sortKey ?? null;
      for (const text of p.items.map((t) => t.trim()).filter(Boolean)) {
        last = keyAfter(last);
        writes.push({ entity: 'listItems', id: env.newId(), patch: { listId, text: text.slice(0, 200), done: false, sortKey: last, createdAt: nowIso, deletedAt: null } });
      }
      return { writes, message: 'assistant.done.list', params: { count: writes.filter((w) => w.entity === 'listItems').length, list: p.listName } };
    }
    case 'log_activity': {
      const h = habit(p.habitId);
      const date = p.date as LocalDate;
      if (p.amount === null) {
        if (dayTotal(data.entries, h.id, date) > 0) return { writes: [], message: 'assistant.done.alreadyDone', params: { name: h.name } };
        return { writes: logWrites(h, date, 1, ctx, data.entries, data.overrides, { newId: env.newId, nowIso }), message: 'assistant.done.marked', params: { name: h.name } };
      }
      return { writes: logWrites(h, date, p.amount, ctx, data.entries, data.overrides, { newId: env.newId, nowIso }), message: 'assistant.done.logged', params: { amount: p.amount, name: h.name } };
    }
    case 'create_note':
      return {
        writes: [{ entity: 'notes', id: env.newId(), patch: { kind: p.noteKind, title: p.title.trim(), body: p.body?.trim() || null, url: null, date: null, tags: [], habitIds: [], pinned: false, createdAt: nowIso, deletedAt: null } }],
        message: `assistant.done.${p.noteKind}`,
      };
    case 'journal':
      return {
        writes: [{ entity: 'journal', id: env.newId(), patch: { date: ctx.today, text: p.text.trim(), prompts: [], includeInReview: false, createdAt: nowIso, deletedAt: null } }],
        message: 'assistant.done.journal',
      };
    case 'update_habit': {
      const h = habit(p.habitId);
      const draft = { ...draftFromHabit(h) };
      if (p.name !== undefined) draft.name = p.name;
      if (p.goalType !== undefined) draft.goalType = p.goalType;
      if (p.target !== undefined) draft.target = p.target;
      if (p.unit !== undefined) draft.unit = p.unit;
      if (p.schedule !== undefined) draft.schedule = p.schedule;
      // Desde hoy: el historial conserva la configuración con la que se registró.
      return { writes: [{ entity: 'habits', id: h.id, patch: habitEditPatch(h, draft, ctx.today, false) }], message: 'assistant.done.updated', params: { name: draft.name } };
    }
    case 'set_reminder': {
      const h = habit(p.habitId);
      const current = data.reminders.find((r) => r.habitId === h.id && !r.deletedAt);
      const message = env.pushActive ? 'assistant.done.reminderSetActive' : 'assistant.done.reminderSetSaved';
      const write: RecordWrite = current
        ? { entity: 'reminders', id: current.id, patch: { habitId: h.id, time: p.time, days: p.days, enabled: true, deletedAt: null } }
        : reminder(h.id, p.time, p.days);
      return { writes: [write], message, params: { name: h.name, time: p.time } };
    }
    case 'reschedule_task': {
      const h = habit(p.habitId);
      if (h.kind !== 'task' || h.completedAt) throw new DomainError('not_found');
      return { writes: [{ entity: 'habits', id: h.id, patch: { dueDate: p.dueDate, dueTime: p.dueDate ? p.dueTime : null } }], message: 'assistant.done.rescheduled', params: { name: h.name } };
    }
    case 'log_focus': {
      if (p.date > ctx.today) throw new DomainError('future_date');
      if (p.habitId) habit(p.habitId);
      const end = p.date === ctx.today ? env.now.getTime() : Date.parse(`${p.date}T12:00:00Z`);
      return {
        writes: [
          {
            entity: 'focus',
            id: env.newId(),
            patch: {
              habitId: p.habitId,
              label: p.label,
              plannedSeconds: p.minutes * 60,
              activeSeconds: p.minutes * 60,
              startedAt: new Date(end - p.minutes * 60_000).toISOString(),
              endedAt: new Date(end).toISOString(),
              logicalDate: p.date,
              completed: true,
              deletedAt: null,
            },
          },
        ],
        message: 'assistant.done.focusLogged',
        params: { count: p.minutes },
      };
    }
    case 'archive_habit': {
      const h = habit(p.habitId);
      return { writes: [{ entity: 'habits', id: h.id, patch: { archivedAt: ctx.today } }], message: 'assistant.done.archived', params: { name: h.name } };
    }
    case 'delete_habit': {
      const h = habit(p.habitId);
      return { writes: [{ entity: 'habits', id: h.id, patch: { deletedAt: nowIso } }], message: 'assistant.done.deleted', params: { name: h.name } };
    }
    case 'show_today':
    case 'start_focus':
      return null;
  }
}
